<script setup lang="ts">
import { useLayout } from '@/layout/composables/layout';
import {AutoCompleteCompleteEvent, AutoCompleteOptionSelectEvent} from "primevue/autocomplete";
import {MailboxService, SearchMailboxResponse} from "@/service/MailboxService.ts";
import {useMonitoring} from "@/composables/useMonitoring";
import {ref} from "vue";
import router from "@/router";
import {AuthenticationService} from "@/service/AuthenticationService.ts";
import {usePermissionStore} from "@/stores/usePermissionStore";

const mailboxService = new MailboxService();
const authService = new AuthenticationService();
const permissionStore = usePermissionStore();
const { trackAdminLogout, trackCustomerSearch } = useMonitoring();

const { onMenuToggle } = useLayout();

const selectedMailbox = ref<SearchMailboxResponse | undefined>(undefined);
const filteredMailboxes = ref<SearchMailboxResponse[]>([]);
const isSearching = ref(false);

const search = async (event: AutoCompleteCompleteEvent) => {
  if (!event.query.trim().length) {
    filteredMailboxes.value = [];
    isSearching.value = false;
    return;
  }

  isSearching.value = true;

  try {
    const results = await mailboxService.search(event.query);
    filteredMailboxes.value = results;

    // Track customer search
    trackCustomerSearch({
      searchTerm: event.query,
      resultCount: results.length
    });
  } catch (error) {
    console.error('Search error:', error);
    filteredMailboxes.value = [];
  } finally {
    isSearching.value = false;
  }
}

async function onMailboxSelected(event: AutoCompleteOptionSelectEvent) {
  selectedMailbox.value = undefined;
  const mailbox = event.value as SearchMailboxResponse;
  if (mailbox) {
    await router.push({
      path: `/mailboxes/${mailbox.id}`
    });
  }
}

async function logout() {
  // Track logout before clearing auth
  trackAdminLogout();

  // Clear authentication and permissions
  authService.logout();
  permissionStore.clearPermissions();

  await router.push({force: true, path: '/'});
}

</script>

<template>
    <div class="layout-topbar shadow-lg">
        <div class="layout-topbar-logo-container">
            <button class="layout-menu-button layout-topbar-action" @click="onMenuToggle">
                <i class="pi pi-bars"></i>
            </button>
            <router-link to="/" class="layout-topbar-logo">
                <img src="/YourMailroom.US-horizontal.png" class="h-9" alt="YourMailroom.US Logo">
            </router-link>
        </div>

        <div class="layout-topbar-actions">
            <button
                class="layout-topbar-menu-button layout-topbar-action"
                v-styleclass="{ selector: '@next', enterFromClass: 'hidden', enterActiveClass: 'animate-scalein', leaveToClass: 'hidden', leaveActiveClass: 'animate-fadeout', hideOnOutsideClick: true }"
            >
                <i class="pi pi-ellipsis-v"></i>
            </button>

            <div class="layout-topbar-menu hidden lg:block">
                <div class="layout-topbar-menu-content">
<!--                    <button type="button" class="layout-topbar-action">-->
<!--                        <i class="pi pi-calendar"></i>-->
<!--                        <span>Calendar</span>-->
<!--                    </button>-->
<!--                    <button type="button" class="layout-topbar-action">-->
<!--                        <i class="pi pi-inbox"></i>-->
<!--                        <span>Messages</span>-->
<!--                    </button>-->
                  <InputGroup>
<!--                    <FloatLabel variant="on">-->
                      <AutoComplete
                          v-model="selectedMailbox"
                          option-label="match"
                          inputId="on_label"
                          :suggestions="filteredMailboxes"
                          @complete="search"
                          forceSelection
                          placeholder="Search Mailbox"
                          fluid
                          :loading="isSearching"
                          @itemSelect="onMailboxSelected"
                      >
                        <template #option="slotProps">
                          <div class="flex flex-col">
                            <div>#{{slotProps.option.number}} - {{ slotProps.option.match }}</div>
<!--                            <small v-if="slotProps.option.knownNames.length > 0">-->
<!--                              <strong>Known names</strong>-->
<!--                              <template v-for="name in slotProps.option.knownNames" :key="name">-->
<!--                                <p>{{ name }}</p>-->
<!--                              </template>-->
<!--                            </small>-->
                          </div>
                        </template>
                        <template #loader>
                          <div class="px-3 py-2 absolute top-0 bottom-0 right-0 z-10">
                            <i class="pi pi-spin pi-spinner"
                               :class="{ 'invisible': !isSearching }"
                            ></i>
                          </div>
                        </template>
                      </AutoComplete>
<!--                      <label for="on_label">Search Mailbox</label>-->
<!--                    </FloatLabel>-->
                  </InputGroup>
                    <button type="button" class="layout-topbar-action">
                        <i class="pi pi-user"></i>
                        <span>Profile</span>
                    </button>
                  <Button icon="pi pi-sign-out" aria-label="Log Out"
                          rounded severity="secondary" variant="text"
                          class="!bg-transparent hover:!bg-primary-50 !border-0"
                          v-tooltip.bottom="'Log Out'"
                          @click="logout"
                  />
                </div>
            </div>
        </div>
    </div>
</template>
